import {
  AudienceFilterType,
  ExpressionNodeQueryType,
  UrlMatch,
} from "@intelligems/ig-types";
import { cookieTargeting } from "@src/utils/linkSheet";

export const pageTargetingDisjointFilters: Partial<
  Record<UrlMatch, Set<UrlMatch>>
> = {
  equals: new Set([
    "equals",
    "doesNotEqual",
    "startsWith",
    "endsWith",
    "doesNotStartWith",
    "doesNotEndWith",
  ]),
  doesNotEqual: new Set([
    "equals",
    "startsWith",
    "endsWith",
    "doesNotStartWith",
    "doesNotEndWith",
  ]),
  contains: new Set(["equals"]),
  doesNotContain: new Set(["equals"]),
  startsWith: new Set([
    "equals",
    "startsWith",
    "doesNotEqual",
    "doesNotStartWith",
  ]),
  doesNotStartWith: new Set(["equals", "doesNotEqual", "startsWith"]),
  endsWith: new Set(["equals", "doesNotEqual", "endsWith", "doesNotEndWith"]),
  doesNotEndWith: new Set(["equals", "doesNotEqual", "doesNotEndWith"]),
};

export type UpdateExpressionNodeQueryType = <
  T extends Exclude<keyof ExpressionNodeQueryType, "type">,
>(
  value: ExpressionNodeQueryType[T],
  changeKey: T
) => void;

export type ConditionTypeOption = {
  key: AudienceFilterType;
  title: string;
  disabled?: boolean;
  tooltipText?: string;
};

export const AudienceConditionTypeOptions: ConditionTypeOption[] = [
  { key: "utm", title: "URL Param" },
  { key: "device", title: "Device" },
  { key: "visitor", title: "New or Returning" },
  { key: "jsExpression", title: "Javascript" },
  { key: "trafficSource", title: "Source Site" },
  { key: "country", title: "Country" },
  { key: "referrer", title: "Referring Domain" },
  { key: "cookie", title: "Cookie" },
  { key: "landingPage", title: "Landing Page" },
];

export const PageTargetingConditionTypeOptions: ConditionTypeOption[] = [
  { key: "urlPath", title: "URL Path" },
  { key: "jsExpression", title: "Javascript" },
];

export interface HintType {
  text: string;
  link?: string;
  tracker?: string;
}

export const hints = {
  cookie: {
    text: "Intelligems can only target first party insecure cookies, for example those of Klaviyo and Google Analytics. Click to learn more.",
    link: cookieTargeting,
    tracker: "Cookie Targeting",
  },
  landingPage: {
    text: "Intelligems will assume https:// protocol. All values are case insensitive. Query params, trailing slashes, www., will be ignored. Special characters will be decoded.",
  },
};

const hasHint = (type: string): type is keyof typeof hints => {
  return type in hints;
};

export const getHint = (type: string): HintType | null => {
  if (hasHint(type)) return hints[type];
  return null;
};

export type ConditionFilterOption = {
  key: UrlMatch;
  title: string;
  disabled?: boolean;
  tooltipText?: string;
};

export const AudienceConditionFilterOptions: ConditionFilterOption[] = [
  { key: "equals", title: "equals" },
  { key: "contains", title: "contains" },
  { key: "startsWith", title: "starts with" },
  { key: "endsWith", title: "ends with" },
  { key: "doesNotEqual", title: "does not equal" },
  { key: "doesNotContain", title: "does not contain" },
  { key: "doesNotStartWith", title: "does not start with" },
  { key: "doesNotEndWith", title: "does not end with" },
  { key: "isNull", title: "is null" },
  { key: "isNotNull", title: "is not null" },
  { key: "regex", title: "regex" },
];

export const PageTargetingConditionFilterOptions: ConditionFilterOption[] = [
  { key: "equals", title: "equals" },
  { key: "contains", title: "contains" },
  { key: "startsWith", title: "starts with" },
  { key: "endsWith", title: "ends with" },
  { key: "doesNotEqual", title: "does not equal" },
  { key: "doesNotContain", title: "does not contain" },
  { key: "doesNotStartWith", title: "does not start with" },
  { key: "doesNotEndWith", title: "does not end with" },
];

export const calculateAllowedPageTargetingFilters = (
  activeFilters: Map<UrlMatch, number>,
  currentFilter: UrlMatch
): Set<UrlMatch> => {
  let allowedFilters: Set<UrlMatch> = new Set(
    Array.from(PageTargetingConditionFilterOptions.map((option) => option.key))
  );

  for (const [key, count] of activeFilters.entries()) {
    // You can always switch to a disjoint filter if
    // this is the only instance of it.
    if (key === currentFilter && count === 1) continue;
    const disjoint = pageTargetingDisjointFilters[key] ?? new Set();
    allowedFilters = allowedFilters.difference(disjoint);
  }

  return allowedFilters;
};

export const pageTargetingUrlPathTypeDisabled = (
  activeFilters?: Map<UrlMatch, number>,
  currentExpression?: ExpressionNodeQueryType
): boolean => {
  const equalsCount = activeFilters?.get("equals") || 0;
  const isUrlEquals =
    currentExpression !== undefined &&
    currentExpression.type === "urlPath" &&
    currentExpression.filter === "equals";

  return equalsCount > 0 && !isUrlEquals;
};

const alternateFilterLabels = {
  landingPage: {
    equals: "matches exactly",
    regex: "matches regex",
  },
  device: {
    equals: "is",
    doesNotEqual: "is not",
  },
  jsExpression: {
    equals: "evaluates to true",
    doesNotEqual: "does not evaluate to true",
  },
  visitor: {
    equals: "is",
    doesNotEqual: "is not",
  },
  country: {
    equals: "is",
    doesNotEqual: "is not",
  },
  trafficSource: {
    equals: "is",
    doesNotEqual: "is not",
  },
};

const hasAlternateFilterLabel = (
  type: string
): type is keyof typeof alternateFilterLabels => {
  return type in alternateFilterLabels;
};

const hasOperatorForType = <T extends keyof typeof alternateFilterLabels>(
  type: T,
  operator: string
): operator is Extract<keyof (typeof alternateFilterLabels)[T], string> => {
  return operator in alternateFilterLabels[type];
};

export const getAlternateFilterLabel = (
  key: UrlMatch,
  type: AudienceFilterType
) => {
  if (hasAlternateFilterLabel(type) && hasOperatorForType(type, key)) {
    return alternateFilterLabels[type][key];
  }

  return null;
};
